'use client';

import { WalletReadyState } from '@solana/wallet-adapter-base';
import { Wallet } from '@solana/wallet-adapter-react';
import { PhantomWalletName } from '@solana/wallet-adapter-wallets';
import { useMemo } from 'react';
import useWalletContext from 'src/hooks/useWalletContext';

const WALLETS_WITH_AUTOCONFIRM: string[] = [PhantomWalletName];

const SUGGESTED_WALLETS: string[] = [];

// the order of these wallets determines their priority in the UI
const PRIORITIZED_WALLETS: string[] = [
	'Mobile Wallet Adapter',
	PhantomWalletName,
	'Backpack',
	'Solflare',
];

export type WalletToDisplayInfo = {
	provider: Wallet;
	featuresAutoConfirm?: boolean;
	suggestedHighlight?: boolean;
};

export const HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY =
	'historicallySelectedWallets';
export const getHistoricallySelectedWallets = () => {
	return JSON.parse(
		localStorage.getItem(HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY) || '[]'
	);
};

const WALLETS_TO_PUT_IN_HIDDEN_SECTION = ['WalletConnect'];

const useWalletsToDisplay = (numberOfWalletsToShow?: number) => {
	const walletContext = useWalletContext();

	const memoizedWallets = useMemo(() => {
		let walletsToShow: WalletToDisplayInfo[] = [];
		const hiddenWallets: WalletToDisplayInfo[] = [];

		if (walletContext?.wallets && walletContext?.wallets?.length > 0) {
			walletContext.wallets
				.slice()
				.sort((walletA, walletB) => {
					// Move phantom to the top of the list
					const prioritizedIndexA = PRIORITIZED_WALLETS.indexOf(
						walletA.adapter?.name
					);
					const prioritizedIndexB = PRIORITIZED_WALLETS.indexOf(
						walletB.adapter?.name
					);

					if (prioritizedIndexA !== -1 && prioritizedIndexB !== -1) {
						return prioritizedIndexA - prioritizedIndexB;
					}

					if (prioritizedIndexA !== -1) {
						return -1;
					}

					if (prioritizedIndexB !== -1) {
						return 1;
					}

					return 0;
				})
				.forEach((walletProvider) => {
					const walletDisplayInfo: WalletToDisplayInfo = {
						provider: walletProvider,
					};

					if (WALLETS_WITH_AUTOCONFIRM.includes(walletProvider.adapter.name)) {
						walletDisplayInfo.featuresAutoConfirm = true;
					}

					if (SUGGESTED_WALLETS.includes(walletProvider.adapter.name)) {
						walletDisplayInfo.suggestedHighlight = true;
					}

					const walletInstalled =
						walletProvider.readyState === WalletReadyState.Installed ||
						walletProvider.readyState === WalletReadyState.Loadable; // this could include wallet adapters that are installed in the codebase (e.g. Solflare)

					const isSuggestedWallet = SUGGESTED_WALLETS.includes(
						walletProvider.adapter.name
					);

					// Only show wallet if it's detected OR it's a suggested wallet
					const shouldShowWallet =
						(walletInstalled &&
							PRIORITIZED_WALLETS.includes(walletProvider.adapter.name)) ||
						isSuggestedWallet;

					if (shouldShowWallet) {
						walletsToShow.push(walletDisplayInfo);
						return;
					}

					const walletShouldBeHidden =
						WALLETS_TO_PUT_IN_HIDDEN_SECTION.includes(
							walletProvider.adapter.name
						);

					const limitWallets = numberOfWalletsToShow !== undefined;

					if (
						walletProvider.adapter.name === 'Solflare' &&
						walletProvider.readyState !== WalletReadyState.Installed
					) {
						// Solflare does something weird where it's "always loadable no matter what", we don't want to highlight it if the user hasn't actually installed it
						hiddenWallets.push(walletDisplayInfo);
						return;
					}

					if (walletShouldBeHidden || !walletInstalled) {
						hiddenWallets.push(walletDisplayInfo);
						return;
					}

					if (!limitWallets) {
						walletsToShow.push(walletDisplayInfo);
						return;
					}

					if (walletsToShow.length < numberOfWalletsToShow) {
						walletsToShow.push(walletDisplayInfo);
						return;
					}

					hiddenWallets.push(walletDisplayInfo);
				});
		}

		// we only show the first MetaMask wallet, because of legacy reasons - we used to integrate MetaMask snap wallets, but have since deprecated them.
		// however if the user does not remove the snap from their MM, it will show up on the list
		walletsToShow = walletsToShow.filter(
			(wallet, index) =>
				wallet.provider.adapter.name !== 'MetaMask' ||
				walletsToShow.findIndex(
					(w) => w.provider.adapter.name === 'MetaMask'
				) === index
		);

		const hiddenDetectedWallets = hiddenWallets.filter(
			(wallet) => wallet.provider.readyState === WalletReadyState.Installed
		);
		const hiddenNonDetectedWallets = hiddenWallets.filter(
			(wallet) => wallet.provider.readyState !== WalletReadyState.Installed
		);

		return {
			walletsToShow,
			hiddenWallets,
			hiddenDetectedWallets,
			hiddenNonDetectedWallets,
		};
	}, [numberOfWalletsToShow, walletContext.wallets]);

	return memoizedWallets;
};

export default useWalletsToDisplay;
